<template>
  <div>
    <div v-if="loading && entries.length === 0" class="d-flex flex-column align-center py-12 text-medium-emphasis" data-test-id="activity-loading">
      <v-progress-circular indeterminate bg-color="transparent" color="muted" size="40"></v-progress-circular>
      <span class="text-body-2 mt-4">Loading history…</span>
    </div>
    <div v-for="entry in entries" :key="entry.id" class="d-flex mb-6" data-test-id="activity">
      <v-avatar color="primary" size="32" class="mr-3 text-caption">{{ entry.initials }}</v-avatar>
      <div>
        <div class="mb-1">
          <strong>{{ entry.name }}</strong>
          <span class="text-caption text-medium-emphasis ml-2">{{ entry.when }}</span>
        </div>
        <div v-for="(sentence, s) in expanded[entry.id] ? entry.sentences : entry.sentences.slice(0, FOLD)" :key="s" class="mb-1" :class="{ 'text-body-2 pl-2': s > 0 }">
          <template v-for="(part, p) in sentence" :key="p">
            <template v-if="typeof part === 'string'">{{ part }}</template>
            <router-link v-else-if="part.kind === 'page' && pageRoute" :to="pageRoute(part.pageId)">{{ part.text }}</router-link>
            <template v-else-if="part.kind === 'page'">{{ part.text }}</template>
            <PageDiff v-else-if="part.kind === 'drawing'" :key="part.key" v-bind="part.pageDiff" />
            <span v-else-if="part.kind === 'swatch'" class="swatch" :style="{ backgroundColor: part.color }" :title="part.color" data-test-id="swatch" />
            <TagDot v-else-if="part.kind === 'dot'" :color="part.color" :title="part.color" class="dot" data-test-id="dot" />
            <span v-else-if="part.kind === 'more'">
              {{ expanded[`${entry.id}-${s}-${p}`] ? part.full : part.text }}
              <a v-if="!expanded[`${entry.id}-${s}-${p}`]" href="#" @click.prevent="expanded[`${entry.id}-${s}-${p}`] = true">show more</a>
            </span>
          </template>
        </div>
        <a v-if="!expanded[entry.id] && entry.sentences.length > FOLD" href="#" class="text-body-2 pl-2" @click.prevent="expanded[entry.id] = true" data-test-id="show-all">show {{ entry.sentences.length - FOLD }} more</a>
      </div>
    </div>
    <v-btn v-if="hasMore" variant="text" color="primary" :loading="loading" @click="load" data-test-id="load-more">Load more</v-btn>
    <p v-if="!loading && !hasMore && entries.length === 0">Once the flatplan is edited, who changed what will be listed here.</p>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from "vue"
import { DateTime } from "luxon"

import { useActivity, PAGE_SIZE } from "../use"
import { group } from "../group"
import { who } from "../describe"
import PageDiff from "./PageDiff"
import TagDot from "@/modules/tag/components/TagDot"
import { Bus } from "@/Bus"

const FOLD = 10 // a longer entry, such as a large content sync, shows this many sentences until expanded
const REFRESH_DELAY = 2000 // a burst of live updates is one fetch

// Fetches the activity rows when mounted, and the newest ones again after live updates from other tabs.
export default {
  name: "ActivityList",
  components: {
    PageDiff,
    TagDot,
  },
  props: {
    pageId: String, // only the rows touching that page
    pageRoute: Function, // pageId => route a page reference links to; without it, page references are plain text
  },
  setup(props) {
    let bp_activity = useActivity()

    let activities = ref([])
    let loading = ref(true)
    let hasMore = ref(false)
    let expanded = reactive({})

    let entries = computed(() => {
      return group(activities.value, bp_activity.ctx.value).map((entry) => ({
        ...entry,
        ...who(entry.actor),
        when: DateTime.fromISO(entry.at).toRelative(),
      }))
    })

    let latest = null // a load overtaken by a change of filter is dropped
    const load = () => {
      let request = (latest = bp_activity.fetchActivities({ olderThan: activities.value.at(-1), pageId: props.pageId }))
      loading.value = true

      return request
        .then((rows) => {
          if (request !== latest) return
          activities.value.push(...rows)
          hasMore.value = rows.length === PAGE_SIZE
        })
        .catch(() => {
          if (request === latest) hasMore.value = false // the error interceptor reports failures; an older data service just has no rows
        })
        .finally(() => {
          if (request === latest) loading.value = false
        })
    }

    watch(
      () => props.pageId,
      () => {
        activities.value = []
        hasMore.value = false
        load()
      }
    )

    // Another tab changed the flatplan: once its burst of live updates has settled, fetch the newest rows
    // and put the ones not shown yet on top. With none in common, start over from them.
    let refreshTimer = null
    const refresh = () => {
      clearTimeout(refreshTimer)
      refreshTimer = setTimeout(() => {
        if (loading.value) return refresh() // try again once the load is done
        let pageId = props.pageId

        bp_activity
          .fetchActivities({ pageId })
          .then((rows) => {
            if (pageId !== props.pageId) return
            let shown = new Set(activities.value.map((activity) => activity._id))
            let fresh = rows.filter((row) => !shown.has(row._id))
            if (fresh.length < rows.length) return activities.value.unshift(...fresh)
            activities.value = rows
            hasMore.value = rows.length === PAGE_SIZE
          })
          .catch(() => {}) // the error interceptor reports failures
      }, REFRESH_DELAY)
    }

    onMounted(() => {
      load()
      Bus.$on("liveupdate-applied", refresh)
    })

    onUnmounted(() => {
      Bus.$off("liveupdate-applied", refresh)
      clearTimeout(refreshTimer)
    })

    return {
      FOLD,
      entries,
      loading,
      hasMore,
      expanded,
      load,
    }
  },
}
</script>

<style lang="scss" scoped>
.swatch {
  display: inline-block;
  width: 0.8em;
  height: 0.8em;
  border: 1px solid rgba(0, 0, 0, 0.3);
  vertical-align: middle;
}

.dot {
  vertical-align: middle;
}
</style>
